import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { UserPlus, Calendar, Clock, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface NewTeacher {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  teaching_experience_years?: number;
  hire_date?: string;
  new_teacher_program_start_date?: string;
  new_teacher_program_end_date?: string;
  mentor_assigned_id?: string;
  mentor_name?: string;
  recent_meetings_count?: number;
  last_meeting_date?: string;
}

interface NewTeacherListProps {
  onAssignmentChange?: () => void;
}

export function NewTeacherList({ onAssignmentChange }: NewTeacherListProps) {
  const [newTeachers, setNewTeachers] = useState<NewTeacher[]>([]);
  const [mentors, setMentors] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadNewTeachers();
    loadMentors();
  }, []);

  const loadNewTeachers = async () => {
    try {
      let teachers = [];
      
      // Try profiles first
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select(`
          id,
          first_name,
          last_name,
          email,
          teaching_experience_years,
          hire_date,
          new_teacher_program_start_date,
          new_teacher_program_end_date,
          mentor_assigned_id
        `)
        .eq('is_new_teacher_program', true)
        .order('last_name');

      if (profileError) console.error('Profile error:', profileError);

      // If no teachers in profiles, use demo_staff
      if (!profileData || profileData.length === 0) {
        const { data: demoData, error: demoError } = await supabase
          .from('demo_staff')
          .select('*, id:employee_id')
          .eq('job_type', 'Teacher')
          .not('position_title', 'in', '(Lead Teacher,Department Head,Master Teacher,Curriculum Specialist)')
          .order('last_name');

        if (demoError) throw demoError;
        
        // Map demo_staff to teacher format
        teachers = (demoData || []).map(teacher => ({
          ...teacher,
          id: teacher.employee_id,
          teaching_experience_years: Math.floor(Math.random() * 3), // 0-2 years for new teachers
          new_teacher_program_start_date: new Date().toISOString().split('T')[0],
          mentor_assigned_id: null
        }));
      } else {
        teachers = profileData;
      }

      // Get mentor information and meeting stats for each new teacher
      const enrichedData = await Promise.all(
        teachers.map(async (teacher) => {
          let mentorName = null;
          let recentMeetingsCount = 0;
          let lastMeetingDate = null;

          // Get mentor name if assigned
          if (teacher.mentor_assigned_id) {
            const { data: mentorData } = await supabase
              .from('profiles')
              .select('first_name, last_name')
              .eq('id', teacher.mentor_assigned_id)
              .single();
            
            if (mentorData) {
              mentorName = `${mentorData.first_name} ${mentorData.last_name}`;
            }

            // Get recent meetings count (last 30 days)
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

            const { data: meetingsData } = await supabase
              .from('meetings')
              .select('scheduled_date, status')
              .eq('observer_id', teacher.mentor_assigned_id)
              .eq('observee_id', teacher.id)
              .eq('meeting_type', 'mentoring')
              .gte('scheduled_date', thirtyDaysAgo.toISOString())
              .order('scheduled_date', { ascending: false });

            if (meetingsData) {
              recentMeetingsCount = meetingsData.length;
              if (meetingsData.length > 0) {
                lastMeetingDate = meetingsData[0].scheduled_date;
              }
            }
          }

          return {
            ...teacher,
            mentor_name: mentorName,
            recent_meetings_count: recentMeetingsCount,
            last_meeting_date: lastMeetingDate
          };
        })
      );

      setNewTeachers(enrichedData);
    } catch (error) {
      console.error('Error loading new teachers:', error);
      toast.error('Failed to load new teachers data');
    } finally {
      setLoading(false);
    }
  };

  const loadMentors = async () => {
    try {
      let mentorsData = [];
      
      // Try profiles first
      const { data: profileMentors, error: profileError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name')
        .eq('is_mentor', true)
        .order('last_name');

      if (profileError) console.error('Profile mentors error:', profileError);

      // If no mentors in profiles, use demo_staff with senior positions
      if (!profileMentors || profileMentors.length === 0) {
        const { data: demoMentors, error: demoError } = await supabase
          .from('demo_staff')
          .select('*, id:employee_id')
          .in('position_title', ['Lead Teacher', 'Department Head', 'Master Teacher', 'Curriculum Specialist', 'Assistant Principal', 'Athletic Director', 'Arts Coordinator'])
          .order('last_name');

        if (demoError) throw demoError;
        
        // Map demo_staff to mentor format
        mentorsData = (demoMentors || []).map(mentor => ({
          id: mentor.employee_id,
          first_name: mentor.first_name,
          last_name: mentor.last_name
        }));
      } else {
        mentorsData = profileMentors;
      }

      setMentors(mentorsData);
    } catch (error) {
      console.error('Error loading mentors:', error);
    }
  };

  const handleAssignMentor = async (teacherId: string, mentorId: string) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ mentor_assigned_id: mentorId })
        .eq('id', teacherId);

      if (error) throw error;

      toast.success('Mentor assigned successfully');
      loadNewTeachers();
      onAssignmentChange?.();
    } catch (error) {
      console.error('Error assigning mentor:', error);
      toast.error('Failed to assign mentor');
    }
  };

  const getComplianceStatus = (teacher: NewTeacher) => {
    const recentMeetings = teacher.recent_meetings_count || 0;
    const requiredMeetings = 2; // Assume 2 meetings per month minimum

    if (!teacher.mentor_assigned_id) {
      return { status: 'No Mentor', variant: 'destructive' as const };
    }
    
    if (recentMeetings >= requiredMeetings) {
      return { status: 'Compliant', variant: 'default' as const };
    } else if (recentMeetings > 0) {
      return { status: 'At Risk', variant: 'secondary' as const };
    } else {
      return { status: 'Non-Compliant', variant: 'destructive' as const };
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center p-8">Loading new teachers...</div>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <UserPlus className="h-5 w-5" />
          New Teachers in Program
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Teacher</TableHead>
              <TableHead>Experience</TableHead>
              <TableHead>Assigned Mentor</TableHead>
              <TableHead>Recent Meetings</TableHead>
              <TableHead>Last Meeting</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {newTeachers.map((teacher) => {
              const compliance = getComplianceStatus(teacher);
              return (
                <TableRow key={teacher.id}>
                  <TableCell>
                    <div>
                      <div className="font-medium">
                        {teacher.first_name} {teacher.last_name}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {teacher.email}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {teacher.teaching_experience_years !== undefined && (
                      <Badge variant="outline">
                        Year {teacher.teaching_experience_years + 1}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {teacher.mentor_name ? (
                      <span className="text-sm">{teacher.mentor_name}</span>
                    ) : (
                      <Badge variant="outline" className="text-destructive">
                        Unassigned
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span>{teacher.recent_meetings_count || 0}</span>
                      {(teacher.recent_meetings_count || 0) < 2 && (
                        <AlertCircle className="h-4 w-4 text-warning" />
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {teacher.last_meeting_date ? (
                      <span className="text-sm">
                        {new Date(teacher.last_meeting_date).toLocaleDateString()}
                      </span>
                    ) : (
                      <span className="text-muted-foreground text-sm">None</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={compliance.variant}>
                      {compliance.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Select
                      value={teacher.mentor_assigned_id || 'unassigned'}
                      onValueChange={(value) => handleAssignMentor(teacher.id, value === 'unassigned' ? '' : value)}
                    >
                      <SelectTrigger className="w-40">
                        <SelectValue placeholder="Assign mentor" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unassigned">No mentor</SelectItem>
                        {mentors.map((mentor) => (
                          <SelectItem key={mentor.id} value={mentor.id}>
                            {mentor.first_name} {mentor.last_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                </TableRow>
              );
            })}
            {newTeachers.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                  No new teachers found in the program.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}